/**
 * Schéma des sections communes et helpers d'affichage — voir besoin-commun-schema.js
 * pour le contexte général (les deux moteurs de rendu qui le partagent).
 *
 * Ce module ne porte que la donnée pure (les sections, leurs champs, leurs
 * options) et les fonctions qui naviguent dedans pour savoir quoi afficher.
 * La validation et le report entre étapes vivent dans besoin-commun-rules.js.
 */
import {
  objectifsPro,
  handicapTypes,
  pmrOptions,
  ouiNonBoolOptions,
  ouiNonStringOptions,
} from '../../components/questionnaire/questionnaire-data.js'

/**
 * Types de champ compris par les deux moteurs de rendu :
 * - 'checkbox-group' : cases à cocher multiples (+ champ « Autre » optionnel)
 * - 'radio'          : choix unique
 * - 'text'           : saisie courte
 * - 'textarea'       : saisie longue
 *
 * `required` accepte aussi une fonction (d) => boolean quand l'obligation ne
 * dépend pas seulement d'être affiché mais d'une réponse précise (cf.
 * pmrCommentaire, dont l'affichage et l'obligation ne coïncident plus depuis
 * BIQ-10).
 */
export const BESOIN_COMMUN_SECTIONS = [
  {
    key: 'objectif',
    title: 'Objectif professionnel de la formation',
    icon: 'fas fa-bullseye',
    storageKey: 'besoin-objectif',
    objective: 'Objectif : identifier ce que le client attend de la formation.',
    // Section à question unique : le back-office porte le libellé dans le titre
    // de section (et l'astérisque avec), le questionnaire le porte sur le champ.
    requiredSection: true,
    fields: [
      {
        key: 'objectifsPro',
        label: 'Objectif professionnel de la formation',
        type: 'checkbox-group',
        options: objectifsPro,
        default: () => [],
        required: true,
        requiredMessage: 'Veuillez sélectionner au moins un objectif professionnel.',
        // Champ libre affiché quand l'option « Autre » est cochée.
        autreKey: 'objectifsAutre',
        autreOption: 'autre',
        autreRequired: true,
        autrePlaceholder: 'Précisez…',
        autreLabel: 'Objectif professionnel — précisez « Autre »',
        autreRequiredMessage: 'Veuillez préciser l\'objectif « Autre ».',
      },
    ],
  },
  {
    key: 'adaptation',
    title: 'Adaptation de la formation au public',
    icon: 'fas fa-users',
    storageKey: 'besoin-adaptation',
    objective: 'Objectif : ajuster le rythme, le langage et la pédagogie.',
    fields: [
      {
        key: 'typologieStagiaires',
        label: 'Typologie des stagiaires',
        type: 'textarea',
        rows: 2,
        placeholder: 'Ex : Administratifs, techniciens, équipe terrain, mixte…',
        default: () => '',
        required: true,
        requiredMessage: 'Veuillez décrire la typologie des stagiaires.',
      },
      {
        key: 'formationDejaSuivie',
        label: 'Formation déjà suivie par les stagiaires ?',
        type: 'radio',
        options: ouiNonStringOptions,
        default: () => 'non',
        required: true,
        requiredMessage: 'Veuillez indiquer si la formation a déjà été suivie.',
      },
      {
        key: 'formationDejaSuivieLaquelle',
        parent: 'formationDejaSuivie',
        label: 'Laquelle ?',
        type: 'text',
        placeholder: 'Nom de la formation',
        default: () => '',
        showIf: (d) => d.formationDejaSuivie === 'oui',
        required: true,
        requiredMessage: 'Veuillez préciser la formation déjà suivie.',
        missingLabel: 'Formation déjà suivie — laquelle',
      },
      {
        key: 'formationDejaSuivieAnnee',
        parent: 'formationDejaSuivie',
        label: 'Année approximative',
        type: 'text',
        placeholder: 'ex : 2023',
        default: () => '',
        showIf: (d) => d.formationDejaSuivie === 'oui',
      },
      {
        key: 'nonFrancophones',
        label: 'Présence de non-francophones ?',
        type: 'radio',
        options: ouiNonBoolOptions,
        default: () => false,
        required: true,
        requiredMessage: 'Veuillez indiquer la présence éventuelle de non-francophones.',
      },
      {
        key: 'nonFrancophoneCommentaire',
        parent: 'nonFrancophones',
        label: 'Précisez',
        type: 'textarea',
        rows: 2,
        placeholder: 'Langues parlées, niveau de compréhension…',
        default: () => '',
        showIf: (d) => d.nonFrancophones === true,
        required: true,
        requiredMessage: 'Veuillez préciser la situation des non-francophones.',
        missingLabel: 'Non-francophones — précisez',
      },
    ],
  },
  {
    key: 'accessibilite',
    title: 'Accessibilité et situations particulières',
    icon: 'fas fa-universal-access',
    storageKey: 'besoin-accessibilite',
    objective: 'Objectif : garantir l\'accessibilité et une adaptation pédagogique appropriée.',
    fields: [
      {
        key: 'handicap',
        label: 'Personnes en situation de handicap nécessitant un aménagement ?',
        type: 'radio',
        options: ouiNonBoolOptions,
        default: () => false,
        required: true,
        requiredMessage: 'Veuillez indiquer la présence éventuelle de personnes en situation de handicap.',
      },
      {
        key: 'handicapTypes',
        parent: 'handicap',
        label: 'Type(s) de handicap',
        type: 'checkbox-group',
        options: handicapTypes,
        default: () => [],
        showIf: (d) => d.handicap === true,
        required: true,
        requiredMessage: 'Veuillez indiquer le ou les types de handicap.',
      },
      {
        key: 'handicapCommentaire',
        parent: 'handicap',
        label: 'Précisez les aménagements',
        type: 'textarea',
        rows: 2,
        placeholder: 'Préciser les aménagements nécessaires (adaptation pédagogique, matérielle, rythme/horaires...)',
        default: () => '',
        showIf: (d) => d.handicap === true,
        required: true,
        requiredMessage: 'Veuillez préciser les aménagements nécessaires.',
        missingLabel: 'Aménagements — précisez',
      },
      {
        key: 'restrictionsMedicales',
        label: 'Restrictions médicales connues ?',
        type: 'radio',
        options: ouiNonBoolOptions,
        default: () => false,
        required: true,
        requiredMessage: 'Veuillez indiquer les restrictions médicales éventuelles.',
      },
      {
        key: 'restrictionsMedicalesDetail',
        parent: 'restrictionsMedicales',
        label: 'Précisez',
        type: 'textarea',
        rows: 2,
        placeholder: 'Grossesse, port de charge, etc.',
        default: () => '',
        showIf: (d) => d.restrictionsMedicales === true,
        required: true,
        requiredMessage: 'Veuillez préciser les restrictions médicales.',
        missingLabel: 'Restrictions médicales — précisez',
      },
      {
        key: 'pmr',
        label: 'Locaux accessibles PMR ?',
        type: 'radio',
        options: pmrOptions,
        default: () => '',
        required: true,
        requiredMessage: 'Veuillez indiquer si les locaux sont accessibles PMR.',
      },
      {
        key: 'pmrCommentaire',
        parent: 'pmr',
        label: 'Commentaire',
        type: 'textarea',
        rows: 2,
        placeholder: 'Précisez la situation des locaux…',
        default: () => '',
        // Reste affiché (et donc corrigeable) dès qu'une valeur existe déjà,
        // même si le PMR a depuis été requalifié en « Oui » ou « Non concerné » :
        // sinon la réponse enregistrée disparaît de l'écran sans que personne
        // ne puisse plus la lire ni la corriger (BIQ-10).
        showIf: (d) => d.pmr === 'non' || Boolean(d.pmrCommentaire),
        // L'obligation, elle, ne concerne que le cas « Non » : elle ne doit
        // pas se déclencher simplement parce que le champ est visible.
        required: (d) => d.pmr === 'non',
        requiredMessage: 'Veuillez commenter l\'inaccessibilité des locaux.',
        missingLabel: 'Locaux PMR — commentaire',
      },
    ],
  },
]

/** Tous les champs des sections communes, à plat. */
export const BESOIN_COMMUN_FIELDS = BESOIN_COMMUN_SECTIONS.flatMap((s) => s.fields)

/**
 * Toutes les clés de données des sections communes, champs « Autre » compris.
 * C'est la liste exacte de ce qui voyage de l'étape 1 vers l'étape 2.
 */
export const BESOIN_COMMUN_KEYS = BESOIN_COMMUN_FIELDS.flatMap((f) =>
  f.autreKey ? [f.key, f.autreKey] : [f.key]
)

/** Valeurs par défaut des sections communes (mêmes défauts des deux côtés). */
export function createEmptyBesoinCommun() {
  const data = {}
  for (const field of BESOIN_COMMUN_FIELDS) {
    data[field.key] = field.default ? field.default() : ''
    if (field.autreKey) data[field.autreKey] = ''
  }
  return data
}

/** Un champ conditionnel ne s'affiche (et ne se valide) que si sa condition est remplie. */
export function isFieldVisible(field, data) {
  return typeof field.showIf === 'function' ? field.showIf(data || {}) : true
}

/** Champs à afficher pour une section, dans l'ordre, selon les réponses courantes. */
export function visibleFields(section, data) {
  return section.fields.filter((f) => isFieldVisible(f, data))
}

/** Questions de premier niveau d'une section (les précisions dépendent d'elles). */
export function rootFields(section) {
  return section.fields.filter((f) => !f.parent)
}

/** Précisions rattachées à une question, visibles au vu des réponses courantes. */
export function childFields(section, parentKey, data) {
  return section.fields.filter((f) => f.parent === parentKey && isFieldVisible(f, data))
}

/** Le champ « Autre » d'un groupe de cases n'apparaît que si l'option est cochée. */
export function isAutreVisible(field, data) {
  if (!field.autreKey) return false
  const values = data?.[field.key]
  return Array.isArray(values) && values.includes(field.autreOption || 'autre')
}

/**
 * Une question est obligatoire si `required` vaut true, ou si la fonction
 * qu'il porte le décide au vu des réponses courantes. Ne pas confondre avec
 * `isFieldVisible` : un champ peut être affiché sans être obligatoire (cf.
 * pmrCommentaire, visible dès qu'il a une valeur mais requis seulement quand
 * `pmr === 'non'`).
 */
export function isFieldRequired(field, data) {
  return typeof field.required === 'function' ? field.required(data || {}) : Boolean(field.required)
}

/**
 * Libellé lisible d'une réponse, pour les écrans qui RELISENT les sections
 * communes au lieu de les faire saisir (le récapitulatif « Analyse du besoin »
 * de la fiche demande). Les valeurs stockées sont des codes ('conformite') ou
 * des booléens ; ce sont les `options` du champ qui portent le texte affiché.
 *
 * Renvoie '' quand la question est restée sans réponse, pour que l'appelant
 * puisse simplement ne rien afficher.
 */
export function formatBesoinCommunValue(field, data) {
  const value = data?.[field.key]
  if (value === undefined || value === null || value === '') return ''
  if (Array.isArray(value) && value.length === 0) return ''

  const optionLabel = (v) => field.options?.find((o) => o.value === v)?.label ?? String(v)

  if (Array.isArray(value)) {
    // L'option « Autre » ne dit rien seule : c'est la précision saisie qui
    // porte le sens, elle prend donc sa place dans la liste.
    const autre = isAutreVisible(field, data) ? data?.[field.autreKey] : ''
    return value
      .map((v) => (autre && v === (field.autreOption || 'autre') ? autre : optionLabel(v)))
      .join(' · ')
  }

  if (field.options) return optionLabel(value)
  return String(value)
}

export { BESOIN_COMMUN_ANCIENNES_CLES, anciennesReponsesBesoin } from './besoin-anciennes-reponses.js'
